import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { QaiFieldComponent, QaiTextareaComponent, QaiTrimLeadingDirective, TextareaAutosizeDirective, QaiPasteCleanDirective } from '@qai/ui';
import { DetailsSectionComponent } from 'src/app/pages/self-serve/simulation-builder/components/details-container/details-section.component';
import { AuthorService } from 'src/app/services/self-serve/author.service';
import { ActivatedRoute } from '@angular/router';
import { debounceTime, distinctUntilChanged, skip } from 'rxjs/operators';
import { ISimulationPrompt } from 'src/app/interfaces/self-serve/isimulation-prompt';
import { SimulationConstants } from 'src/app/utils/constants/self-serve/simulation.constants';
import { AuthorReactiveFormService } from 'src/app/pages/self-serve/simulation-builder/services/author-reactive-form.service';

@Component({
  selector: 'methodology-section',
  standalone: true,
  templateUrl: './methodology-section.component.html',
  styleUrls: ['./methodology-section.component.scss'],
  imports: [
    QaiFieldComponent,
    FormsModule,
    CommonModule,
    QaiTextareaComponent,
    TextareaAutosizeDirective,
    QaiTrimLeadingDirective,
    DetailsSectionComponent,
    ReactiveFormsModule,
    QaiPasteCleanDirective
],
})
export class MethodologySectionComponent {
  form!: FormGroup;
  methodologySection!: FormGroup;
  callUuid: string = '';

  methodologyPromptLabel: string =
    'Enter the prompt that will be used to score the Methodology section of the role play. *';

  constructor(
    private authorService: AuthorService,
    private route: ActivatedRoute,
    private authoReactiveFormService: AuthorReactiveFormService
  ) {}

  ngOnInit(): void {
    this.callUuid = this.route.snapshot.paramMap.get('uuid')!;
    this.loadFormAndSetupAutoSave();
  }

  private setReadOnlyMode() {
    this.authoReactiveFormService.setReadOnlyMode(
      this.callUuid,
      this.methodologyPromptGroup?.get('promptText')
    );
  }

  get methodologyPromptGroup(): FormGroup {
    return this.form?.get(
      SimulationConstants.METHODOLOGY_PROMPT_SECTION
    ) as FormGroup;
  }

  private loadFormAndSetupAutoSave(): void {
    this.authoReactiveFormService.getForm(this.callUuid)?.subscribe((form) => {
      this.form = form?.get('scoring') as FormGroup;
      this.methodologySection = form?.get(
        SimulationConstants.METHODOLOGY_PROMPT_PATH
      ) as FormGroup;
      this.setupAutoSave(
        this.methodologySection,
        SimulationConstants.METHODOLOGY_PROMPT_PATH
      );

      this.setReadOnlyMode();
    });
  }

   private setupAutoSave(section: FormGroup, formPath: string): void {
    const promptTextControl = section?.get('promptText');

    if (!promptTextControl) return;

    let previousValue: string = promptTextControl.getRawValue();

    promptTextControl.valueChanges
      .pipe(
        skip(1), // ignore initial form init change
        debounceTime(SimulationConstants.AUTOSAVE_GENERIC_DEBOUNCE),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
      )
      .subscribe((newVal) => {
        const oldValue = previousValue;
        previousValue = newVal;

        const updatedValue = section.getRawValue();

        const trimmedOld = (oldValue || '').trim();
        const trimmedNew = (newVal || '').trim();

        if (trimmedOld === trimmedNew && trimmedNew === '') {
          return;
        }

        this.authoReactiveFormService.patchForm(
          this.callUuid,
          formPath,
          updatedValue
        );

        const readOnly = this.authoReactiveFormService.isReadOnly(
          this.callUuid
        );

        if (updatedValue && !readOnly) {
          this.triggerPromptSave(this.callUuid, updatedValue, formPath);
        }
      });
  }

  private triggerPromptSave(
    callUuid: string,
    prompt: ISimulationPrompt,
    formPath: string
  ): void {
    const payload = { prompts: [prompt] };

    const request$ = prompt.uuid
      ? this.authorService.updateSimulationPrompts(callUuid, payload.prompts)
      : this.authorService.createSimulationPrompts(callUuid, payload.prompts);

    request$?.subscribe((response) => {
      const updatedPrompt = response?.prompts?.[0];
      if (!updatedPrompt?.uuid) return;

      this.authoReactiveFormService.patchForm(callUuid, formPath, {
        uuid: updatedPrompt.promptText === "" ? null : updatedPrompt.uuid,
      });

      this.form.updateValueAndValidity();
    });
  }
}
